/**
 * SQ400H Scanner Commands — Frontend Reference
 * Prefix for Code 128 setting barcodes: "+N+S-"
 */

export const BARCODE_PREFIX = '+N+S-';

export const CATEGORIES = {
  communication: { icon: 'Wifi', color: '#3b82f6' },
  scanning: { icon: 'ScanLine', color: '#8b5cf6' },
  illumination: { icon: 'Lightbulb', color: '#f59e0b' },
  feedback: { icon: 'Volume2', color: '#22c55e' },
  data_format: { icon: 'FileText', color: '#06b6d4' },
  symbologies: { icon: 'Barcode', color: '#ec4899' },
  system: { icon: 'Settings', color: '#6b7280' },
};

export const SCANNER_COMMANDS = {
  HOST_MODE: {
    operand: 0x41, name: 'Host Interface Mode', nameHe: 'מצב ממשק', category: 'communication',
    description: 'Sets how the scanner connects to the host computer. HID = acts as keyboard, CDC = serial port for programming, Bluetooth = wireless.',
    descriptionHe: 'קובע איך הסורק מתחבר למחשב. HID = פועל כמקלדת, CDC = פורט סיריאלי לתכנות, בלוטות\' = אלחוטי.',
    options: [
      { label: 'USB HID Keyboard', labelHe: 'מקלדת USB HID', value: 0x01, isDefault: true },
      { label: 'USB Virtual Serial (CDC)', labelHe: 'סיריאלי וירטואלי USB', value: 0x02 },
      { label: 'Bluetooth', labelHe: 'בלוטות\'', value: 0x03 },
      { label: 'RS232 Serial', labelHe: 'סיריאלי RS232', value: 0x04 },
      { label: 'HID-POS', labelHe: 'HID-POS', value: 0x05 },
    ],
  },
  BAUD_RATE: {
    operand: 0x48, name: 'Baud Rate', nameHe: 'קצב שידור', category: 'communication',
    description: 'Communication speed for serial connection. Both scanner and host must use the same baud rate.',
    descriptionHe: 'מהירות התקשורת בחיבור סיריאלי. הסורק והמחשב חייבים להיות באותו קצב.',
    options: [
      { label: '115200', value: 0x00 }, { label: '38400', value: 0x01 }, { label: '19200', value: 0x02 },
      { label: '9600', value: 0x03, isDefault: true }, { label: '4800', value: 0x04 },
      { label: '2400', value: 0x05 }, { label: '1200', value: 0x06 },
    ],
  },
  PARITY: {
    operand: 0x49, name: 'Parity', nameHe: 'בדיקת זוגיות', category: 'communication',
    description: 'Error checking method for serial communication. Must match the host setting.',
    descriptionHe: 'שיטת בדיקת שגיאות בתקשורת סיריאלית. חייב להתאים להגדרת המחשב.',
    options: [
      { label: 'None', labelHe: 'ללא', value: 0x00, isDefault: true },
      { label: 'Odd', labelHe: 'אי-זוגי', value: 0x01 },
      { label: 'Even', labelHe: 'זוגי', value: 0x02 },
    ],
  },
  HID_SPEED: {
    operand: 0x4E, name: 'HID Output Speed', nameHe: 'מהירות פלט HID', category: 'communication',
    description: 'How fast the scanner types characters in HID mode. Slow down if characters are lost.',
    descriptionHe: 'מהירות הקלדת התווים במצב מקלדת. הורד מהירות אם תווים נאבדים.',
    options: [
      { label: 'Fast', labelHe: 'מהיר', value: 0x01 },
      { label: 'Normal', labelHe: 'רגיל', value: 0x02, isDefault: true },
      { label: 'Slow', labelHe: 'איטי', value: 0x0A },
    ],
  },
  BT_DATA_MODE: {
    operand: 0x71, name: 'Bluetooth Data Mode', nameHe: 'מצב נתוני בלוטות\'', category: 'communication',
    description: 'Bluetooth protocol. HID = keyboard, GATT = custom data service.',
    descriptionHe: 'פרוטוקול בלוטות\'. HID = מקלדת, GATT = שירות נתונים מותאם.',
    options: [
      { label: 'HID', value: 0x00, isDefault: true },
      { label: 'GATT', value: 0x01 },
    ],
  },
  TRIGGER_MODE: {
    operand: 0x42, name: 'Scanning Mode', nameHe: 'מצב סריקה', category: 'scanning',
    description: 'Manual = scan on trigger press. Continuous = non-stop scanning. Auto Sensing = auto-detects barcodes.',
    descriptionHe: 'ידני = סורק בלחיצה. רציף = סורק ללא הפסקה. חישה אוטומטית = מזהה ברקודים אוטומטית.',
    options: [
      { label: 'Manual Trigger', labelHe: 'ידני', value: 0x00, isDefault: true },
      { label: 'Continuous', labelHe: 'רציף', value: 0x01 },
      { label: 'Auto Sensing', labelHe: 'חישה אוטומטית', value: 0x02 },
    ],
  },
  DECODE_TIMEOUT: {
    operand: 0x44, name: 'Decode Timeout', nameHe: 'זמן קצוב לפענוח', category: 'scanning',
    description: 'Max time to try reading a barcode before giving up. Higher = more time.',
    descriptionHe: 'זמן מקסימלי לניסיון קריאת ברקוד. גבוה יותר = יותר זמן.',
    type: 'range', min: 1, max: 99, defaultValue: 99, unit: '× 0.1s',
  },
  SENSITIVITY: {
    operand: 0x51, name: 'Sensitivity', nameHe: 'רגישות', category: 'scanning',
    description: 'Detection sensitivity in auto-sensing mode. Ultra-Fast may reduce distance.',
    descriptionHe: 'רגישות זיהוי במצב חישה. מהיר מאוד עלול לצמצם טווח.',
    options: [
      { label: 'Normal', labelHe: 'רגיל', value: 0x00, isDefault: true },
      { label: 'Fast', labelHe: 'מהיר', value: 0x01 },
      { label: 'Ultra-Fast', labelHe: 'מהיר מאוד', value: 0x02 },
    ],
  },
  CENTER_AIMING: {
    operand: 0x53, name: 'Center Aiming', nameHe: 'כיוון מרכזי', category: 'scanning',
    description: 'Only reads barcodes centered in the aiming beam. Prevents scanning wrong barcode.',
    descriptionHe: 'קורא רק ברקודים במרכז קו הכיוון. מונע סריקת ברקוד שגוי.',
    options: [
      { label: 'Enable', labelHe: 'מופעל', value: 0x01, isDefault: true },
      { label: 'Disable', labelHe: 'מושבת', value: 0x00 },
    ],
  },
  SCREEN_SCAN: {
    operand: 0x6D, name: 'Screen Scan Mode', nameHe: 'מצב סריקת מסך', category: 'scanning',
    description: 'Optimizes for reading barcodes from phone/computer screens.',
    descriptionHe: 'מותאם לקריאת ברקודים ממסכי טלפון/מחשב.',
    options: [
      { label: 'Normal', labelHe: 'רגיל', value: 0x00, isDefault: true },
      { label: 'Screen Optimized', labelHe: 'מותאם למסך', value: 0x01 },
    ],
  },
  MULTI_CODE: {
    operand: 0x4B, name: 'Multi-Code Scanning', nameHe: 'סריקת מולטי-קוד', category: 'scanning',
    description: 'Scan multiple barcodes at once.',
    descriptionHe: 'סריקת מספר ברקודים בו-זמנית.',
    options: [
      { label: 'OFF', labelHe: 'כבוי', value: 0x00, isDefault: true },
      { label: '2', value: 0x02 }, { label: '3', value: 0x03 }, { label: '4', value: 0x04 },
      { label: '5', value: 0x05 }, { label: '6', value: 0x06 }, { label: '7', value: 0x07 },
    ],
  },
  AIMING: {
    operand: 0x46, name: 'Aiming Laser', nameHe: 'לייזר כיוון', category: 'illumination',
    description: 'Red aiming line that helps point at the barcode.',
    descriptionHe: 'קו כיוון אדום שעוזר לכוון לברקוד.',
    options: [
      { label: 'ON', labelHe: 'פעיל', value: 0x01, isDefault: true },
      { label: 'OFF', labelHe: 'כבוי', value: 0x00 },
    ],
  },
  ILLUMINATION: {
    operand: 0x47, name: 'Illumination Level', nameHe: 'עוצמת תאורה', category: 'illumination',
    description: 'White LED brightness. 0 = off, 10 = max.',
    descriptionHe: 'בהירות נורית LED. 0 = כבוי, 10 = מקסימום.',
    type: 'range', min: 0, max: 10, defaultValue: 10, labels: { 0: 'OFF' },
  },
  BUZZER: {
    operand: 0x4D, name: 'Buzzer', nameHe: 'זמזם', category: 'feedback',
    description: 'Beep sounds. Power ON = beep at startup. Good Read = beep after successful scan.',
    descriptionHe: 'צלילי ביפ. הפעלה = ביפ בהדלקה. קריאה מוצלחת = ביפ אחרי סריקה.',
    options: [
      { label: 'Power ON + Good Read ON', labelHe: 'הפעלה + קריאה מוצלחת', value: 0x01, isDefault: true },
      { label: 'All OFF', labelHe: 'הכל כבוי', value: 0x00 },
      { label: 'Good Read Only', labelHe: 'קריאה מוצלחת בלבד', value: 0x02 },
      { label: 'Power ON Only', labelHe: 'הפעלה בלבד', value: 0x03 },
    ],
  },
  VIBRATOR: {
    operand: 0x72, name: 'Vibrator', nameHe: 'רטט', category: 'feedback',
    description: 'Vibration on successful scan. Useful in noisy places.',
    descriptionHe: 'רטט בסריקה מוצלחת. שימושי במקומות רועשים.',
    options: [
      { label: 'Disable', labelHe: 'מושבת', value: 0x00 },
      { label: 'Enable', labelHe: 'מופעל', value: 0x01 },
    ],
  },
  DATA_SUFFIX: {
    operand: 0x45, name: 'Data Suffix', nameHe: 'סיומת נתונים', category: 'data_format',
    description: 'Character added after barcode data. CR = Enter key. TAB = next field. Common for POS.',
    descriptionHe: 'תו שמתווסף אחרי הנתונים. CR = אנטר. TAB = שדה הבא. נפוץ לקופות.',
    type: 'dual_byte',
    presets: [
      { label: 'None', labelHe: 'ללא', value: [0x00, 0x00] },
      { label: 'CR (Enter)', labelHe: 'CR (אנטר)', value: [0x0D, 0x00] },
      { label: 'LF', value: [0x0A, 0x00] },
      { label: 'CR+LF', value: [0x0D, 0x0A] },
      { label: 'TAB', value: [0x09, 0x00] },
    ],
    defaultValue: [0x0A, 0x00],
  },
  DATA_PREFIX: {
    operand: 0x52, name: 'Data Prefix', nameHe: 'תחילית נתונים', category: 'data_format',
    description: 'Character added before barcode data. STX used in medical/POS protocols.',
    descriptionHe: 'תו שמתווסף לפני הנתונים. STX משמש בפרוטוקולים רפואיים.',
    type: 'dual_byte',
    presets: [
      { label: 'None', labelHe: 'ללא', value: [0x00, 0x00] },
      { label: 'STX (0x02)', value: [0x02, 0x00] },
    ],
    defaultValue: [0x00, 0x00],
  },
  SKIP_BEFORE: {
    operand: 0x59, name: 'Skip Leading Characters', nameHe: 'דלג על תווים בהתחלה', category: 'data_format',
    description: 'Remove characters from the start of barcode data.',
    descriptionHe: 'מסיר תווים מתחילת נתוני הברקוד.',
    type: 'range', min: 0, max: 31, defaultValue: 0,
  },
  SKIP_AFTER: {
    operand: 0x5A, name: 'Skip Trailing Characters', nameHe: 'דלג על תווים בסוף', category: 'data_format',
    description: 'Remove characters from the end of barcode data.',
    descriptionHe: 'מסיר תווים מסוף נתוני הברקוד.',
    type: 'range', min: 0, max: 31, defaultValue: 0,
  },
  CONTROL_CHAR_MODE: {
    operand: 0x6C, name: 'Control Character Output', nameHe: 'פלט תווי בקרה', category: 'data_format',
    description: 'How control characters (0x01-0x1F) are sent to host.',
    descriptionHe: 'איך תווי בקרה נשלחים למחשב.',
    options: [
      { label: 'As Characters', labelHe: 'כתווים', value: 0x00, isDefault: true },
      { label: 'As Function Keys', labelHe: 'כמקשי פונקציה', value: 0x02 },
    ],
  },

  // ===== SYMBOLOGIES =====
  SYM_ALL_1D: {
    operand: 0x40, name: 'All 1D Barcodes', nameHe: 'כל ברקודים חד-ממדיים', category: 'symbologies',
    description: 'Enable or disable all 1D barcode types at once (EAN, UPC, Code 128, Code 39, etc).',
    descriptionHe: 'הפעל או כבה את כל סוגי הברקוד החד-ממדיים בבת אחת.',
    type: 'symbology_toggle',
    options: [
      { label: 'Enable All 1D', labelHe: 'הפעל הכל 1D', barcodeData: [0x2B, 0x4E, 0x2B, 0x53, 0x2D, 0x40, 0xFF, 0x01] },
      { label: 'Disable All 1D', labelHe: 'כבה הכל 1D', barcodeData: [0x2B, 0x4E, 0x2B, 0x53, 0x2D, 0x40, 0xFF, 0x00] },
    ],
  },
  SYM_ALL_2D: {
    operand: 0x40, name: 'All 2D Barcodes', nameHe: 'כל ברקודים דו-ממדיים', category: 'symbologies',
    description: 'Enable or disable all 2D barcode types at once (QR, Data Matrix, PDF417, Aztec).',
    descriptionHe: 'הפעל או כבה את כל סוגי הברקוד הדו-ממדיים בבת אחת.',
    type: 'symbology_toggle',
    options: [
      { label: 'Enable All 2D', labelHe: 'הפעל הכל 2D', barcodeData: [0x2B, 0x4E, 0x2B, 0x53, 0x2D, 0x40, 0xFE, 0x01] },
      { label: 'Disable All 2D', labelHe: 'כבה הכל 2D', barcodeData: [0x2B, 0x4E, 0x2B, 0x53, 0x2D, 0x40, 0xFE, 0x00] },
    ],
  },
  SYM_EAN8: {
    operand: 0x40, name: 'EAN-8', nameHe: 'EAN-8', category: 'symbologies',
    description: 'Short 8-digit European barcode for small products.',
    descriptionHe: 'ברקוד אירופי קצר בן 8 ספרות למוצרים קטנים.',
    type: 'symbology_enable', param: 0x04,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_EAN13: {
    operand: 0x40, name: 'EAN-13', nameHe: 'EAN-13', category: 'symbologies',
    description: 'Standard 13-digit barcode. Most common on retail products worldwide.',
    descriptionHe: 'ברקוד 13 ספרות. הנפוץ ביותר על מוצרים קמעונאיים בעולם.',
    type: 'symbology_enable', param: 0x05,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_UPCA: {
    operand: 0x40, name: 'UPC-A', nameHe: 'UPC-A', category: 'symbologies',
    description: '12-digit American retail barcode for US/Canada.',
    descriptionHe: 'ברקוד קמעונאי אמריקאי בן 12 ספרות.',
    type: 'symbology_enable', param: 0x01,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_UPCE: {
    operand: 0x40, name: 'UPC-E', nameHe: 'UPC-E', category: 'symbologies',
    description: 'Compressed 8-digit UPC for small packages.',
    descriptionHe: 'UPC מכווץ בן 8 ספרות לאריזות קטנות.',
    type: 'symbology_enable', param: 0x02,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_CODE128: {
    operand: 0x40, name: 'Code 128', nameHe: 'Code 128', category: 'symbologies',
    description: 'High-density barcode for shipping and logistics. Supports all ASCII.',
    descriptionHe: 'ברקוד בצפיפות גבוהה למשלוחים ולוגיסטיקה. תומך בכל ASCII.',
    type: 'symbology_enable', param: 0x08,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_CODE39: {
    operand: 0x40, name: 'Code 39', nameHe: 'Code 39', category: 'symbologies',
    description: 'Alphanumeric barcode for automotive, defense, healthcare.',
    descriptionHe: 'ברקוד אלפאנומרי לרכב, ביטחון ובריאות.',
    type: 'symbology_enable', param: 0x0B,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_CODE93: {
    operand: 0x40, name: 'Code 93', nameHe: 'Code 93', category: 'symbologies',
    description: 'Compact barcode used by Canada Post.',
    descriptionHe: 'ברקוד קומפקטי בשימוש דואר קנדה.',
    type: 'symbology_enable', param: 0x0D,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_CODE11: {
    operand: 0x40, name: 'Code 11', nameHe: 'Code 11', category: 'symbologies',
    description: 'Numeric barcode used for labeling telecom equipment.',
    descriptionHe: 'ברקוד מספרי לתיוג ציוד תקשורת.',
    type: 'symbology_enable', param: 0x0E,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 0 }, { label: 'Disable', labelHe: 'מושבת', value: 0, isDefault: true }],
  },
  SYM_CODABAR: {
    operand: 0x40, name: 'Codabar', nameHe: 'Codabar', category: 'symbologies',
    description: 'Used in libraries, blood banks, and FedEx airbills.',
    descriptionHe: 'משמש בספריות, בנקי דם ו-FedEx.',
    type: 'symbology_enable', param: 0x14,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_ITF: {
    operand: 0x40, name: 'Interleaved 2 of 5', nameHe: 'Interleaved 2 of 5', category: 'symbologies',
    description: 'Compact numeric barcode for carton labels and warehousing.',
    descriptionHe: 'ברקוד מספרי קומפקטי לתוויות קרטונים ומחסנים.',
    type: 'symbology_enable', param: 0x0F,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_MSI: {
    operand: 0x40, name: 'MSI Plessey', nameHe: 'MSI Plessey', category: 'symbologies',
    description: 'Numeric barcode for grocery store shelf labeling.',
    descriptionHe: 'ברקוד מספרי לתיוג מדפים בסופרמרקטים.',
    type: 'symbology_enable', param: 0x15,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1 }, { label: 'Disable', labelHe: 'מושבת', value: 0, isDefault: true }],
  },
  SYM_GS1: {
    operand: 0x40, name: 'GS1 DataBar', nameHe: 'GS1 DataBar', category: 'symbologies',
    description: 'Compact barcode for fresh food, coupons, small items.',
    descriptionHe: 'ברקוד קומפקטי למזון טרי, קופונים ופריטים קטנים.',
    type: 'symbology_enable', param: 0x1E,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_QR: {
    operand: 0x40, name: 'QR Code', nameHe: 'QR Code', category: 'symbologies',
    description: '2D barcode for URLs, text, payments. Common on mobile.',
    descriptionHe: 'ברקוד דו-ממדי לקישורים, טקסט ותשלומים. נפוץ בנייד.',
    type: 'symbology_enable', param: 0x28,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_DATAMATRIX: {
    operand: 0x40, name: 'Data Matrix', nameHe: 'Data Matrix', category: 'symbologies',
    description: '2D barcode for electronics, medical devices, industrial parts.',
    descriptionHe: 'ברקוד דו-ממדי לאלקטרוניקה, מכשור רפואי וחלקים תעשייתיים.',
    type: 'symbology_enable', param: 0x26,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_PDF417: {
    operand: 0x40, name: 'PDF417', nameHe: 'PDF417', category: 'symbologies',
    description: '2D stacked barcode for ID cards, licenses, boarding passes.',
    descriptionHe: 'ברקוד דו-ממדי מוערם לתעודות זהות, רישיונות וכרטיסי טיסה.',
    type: 'symbology_enable', param: 0x24,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_AZTEC: {
    operand: 0x40, name: 'Aztec', nameHe: 'Aztec', category: 'symbologies',
    description: '2D barcode for transport tickets and boarding passes.',
    descriptionHe: 'ברקוד דו-ממדי לכרטיסי תחבורה וטיסה.',
    type: 'symbology_enable', param: 0x2A,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },
  SYM_HANXIN: {
    operand: 0x40, name: 'Han Xin', nameHe: 'Han Xin', category: 'symbologies',
    description: '2D barcode for Chinese characters. Used in Chinese logistics.',
    descriptionHe: 'ברקוד דו-ממדי לתווים סיניים. משמש בלוגיסטיקה בסין.',
    type: 'symbology_enable', param: 0x2B,
    options: [{ label: 'Enable', labelHe: 'מופעל', value: 1, isDefault: true }, { label: 'Disable', labelHe: 'מושבת', value: 0 }],
  },

  // ===== BARCODE LENGTH LIMITS =====
  LEN_CODE128: {
    operand: 0x50, name: 'Code 128 Length', nameHe: 'אורך Code 128', category: 'data_format',
    description: 'Set minimum and maximum length for Code 128 barcodes (0-55). Set both to 0 for unlimited.',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Code 128 (0-55). הגדר 0 לללא הגבלה.',
    type: 'barcode_length', paramMin: 0x0151, paramMax: 0x0152, rangeMin: 0, rangeMax: 55, defaultMin: 0, defaultMax: 0,
  },
  LEN_CODE39: {
    operand: 0x50, name: 'Code 39 Length', nameHe: 'אורך Code 39', category: 'data_format',
    description: 'Set minimum and maximum length for Code 39 barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Code 39 (0-55).',
    type: 'barcode_length', paramMin: 0x0012, paramMax: 0x0013, rangeMin: 0, rangeMax: 55, defaultMin: 2, defaultMax: 55,
  },
  LEN_CODE93: {
    operand: 0x50, name: 'Code 93 Length', nameHe: 'אורך Code 93', category: 'data_format',
    description: 'Set minimum and maximum length for Code 93 barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Code 93 (0-55).',
    type: 'barcode_length', paramMin: 0x001A, paramMax: 0x001B, rangeMin: 0, rangeMax: 55, defaultMin: 4, defaultMax: 55,
  },
  LEN_CODE11: {
    operand: 0x50, name: 'Code 11 Length', nameHe: 'אורך Code 11', category: 'data_format',
    description: 'Set minimum and maximum length for Code 11 barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Code 11 (0-55).',
    type: 'barcode_length', paramMin: 0x001C, paramMax: 0x001D, rangeMin: 0, rangeMax: 55, defaultMin: 4, defaultMax: 55,
  },
  LEN_ITF: {
    operand: 0x50, name: 'Interleaved 2/5 Length', nameHe: 'אורך Interleaved 2/5', category: 'data_format',
    description: 'Set minimum and maximum length for Interleaved 2 of 5 barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Interleaved 2/5 (0-55).',
    type: 'barcode_length', paramMin: 0x0016, paramMax: 0x0017, rangeMin: 0, rangeMax: 55, defaultMin: 4, defaultMax: 55,
  },
  LEN_DTF: {
    operand: 0x50, name: 'Discrete 2/5 Length', nameHe: 'אורך Discrete 2/5', category: 'data_format',
    description: 'Set minimum and maximum length for Discrete 2 of 5 barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Discrete 2/5 (0-55).',
    type: 'barcode_length', paramMin: 0x0014, paramMax: 0x0015, rangeMin: 0, rangeMax: 55, defaultMin: 4, defaultMax: 55,
  },
  LEN_CODABAR: {
    operand: 0x50, name: 'Codabar Length', nameHe: 'אורך Codabar', category: 'data_format',
    description: 'Set minimum and maximum length for Codabar barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד Codabar (0-55).',
    type: 'barcode_length', paramMin: 0x0018, paramMax: 0x0019, rangeMin: 0, rangeMax: 55, defaultMin: 5, defaultMax: 55,
  },
  LEN_MSI: {
    operand: 0x50, name: 'MSI Length', nameHe: 'אורך MSI', category: 'data_format',
    description: 'Set minimum and maximum length for MSI barcodes (0-55).',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לברקוד MSI (0-55).',
    type: 'barcode_length', paramMin: 0x001E, paramMax: 0x001F, rangeMin: 0, rangeMax: 55, defaultMin: 4, defaultMax: 55,
  },
  LEN_GS1_DATABAR: {
    operand: 0x50, name: 'GS1 DataBar Length', nameHe: 'אורך GS1 DataBar', category: 'data_format',
    description: 'Set minimum and maximum length for GS1 DataBar (0-255). Set both to 0 for unlimited.',
    descriptionHe: 'הגדר אורך מינימום ומקסימום ל-GS1 DataBar (0-255). הגדר 0 לללא הגבלה.',
    type: 'barcode_length', paramMin: 0x0A76, paramMax: 0x0A77, rangeMin: 0, rangeMax: 255, defaultMin: 0, defaultMax: 0,
  },
  LEN_QR: {
    operand: 0x50, name: 'QR Code Length', nameHe: 'אורך QR', category: 'data_format',
    description: 'Set minimum and maximum length for QR code data (0-255). Set both to 0 for unlimited.',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לנתוני QR (0-255). הגדר 0 לללא הגבלה.',
    type: 'barcode_length', paramMin: 0x0A78, paramMax: 0x0A79, rangeMin: 0, rangeMax: 255, defaultMin: 0, defaultMax: 0,
  },
  LEN_PDF417: {
    operand: 0x50, name: 'PDF417 Length', nameHe: 'אורך PDF417', category: 'data_format',
    description: 'Set minimum and maximum length for PDF417 data (0-255). Set both to 0 for unlimited.',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לנתוני PDF417 (0-255). הגדר 0 לללא הגבלה.',
    type: 'barcode_length', paramMin: 0x0A7A, paramMax: 0x0A7B, rangeMin: 0, rangeMax: 255, defaultMin: 0, defaultMax: 0,
  },
  LEN_DATAMATRIX: {
    operand: 0x50, name: 'Data Matrix Length', nameHe: 'אורך Data Matrix', category: 'data_format',
    description: 'Set minimum and maximum length for Data Matrix data (0-255). Set both to 0 for unlimited.',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לנתוני Data Matrix (0-255). הגדר 0 לללא הגבלה.',
    type: 'barcode_length', paramMin: 0x0A7C, paramMax: 0x0A7D, rangeMin: 0, rangeMax: 255, defaultMin: 0, defaultMax: 0,
  },
  LEN_AZTEC: {
    operand: 0x50, name: 'Aztec Length', nameHe: 'אורך Aztec', category: 'data_format',
    description: 'Set minimum and maximum length for Aztec data (0-255). Set both to 0 for unlimited.',
    descriptionHe: 'הגדר אורך מינימום ומקסימום לנתוני Aztec (0-255). הגדר 0 לללא הגבלה.',
    type: 'barcode_length', paramMin: 0x0A7E, paramMax: 0x0A7F, rangeMin: 0, rangeMax: 255, defaultMin: 0, defaultMax: 0,
  },

  // ===== SYSTEM =====
  FACTORY_RESET: {
    operand: 0x43, name: 'Factory Reset', nameHe: 'איפוס להגדרות יצרן', category: 'system',
    description: 'Resets ALL settings to factory defaults. All custom configuration will be lost!',
    descriptionHe: 'מאפס את כל ההגדרות לברירת מחדל. כל ההגדרות המותאמות יאבדו!',
    data: [0x00], warning: true,
    multiFormat: true,
    formats: ['CODE128', 'QR'],
  },
  READ_FW_VERSION: {
    operand: 0x43, name: 'Read Firmware Version', nameHe: 'קריאת גרסת קושחה', category: 'system',
    description: 'Scanner outputs its firmware version number to the host.',
    descriptionHe: 'הסורק שולח את מספר גרסת הקושחה למחשב.',
    rawContent: 'HYRS_RE',
  },
  READ_SERIAL_NUMBER: {
    operand: 0x43, name: 'Read Serial Number', nameHe: 'קריאת מספר סידורי', category: 'system',
    description: 'Scanner outputs its unique serial number to the host.',
    descriptionHe: 'הסורק שולח את המספר הסידורי הייחודי למחשב.',
    rawContent: 'HYRS_SN',
  },
};

export function calculateChecksum(bytes) {
  const sum = bytes.reduce((acc, b) => acc + b, 0);
  return (~sum) & 0xFF;
}

export function buildSerialCommand(operand, data = [], permanent = true) {
  const length = 1 + 1 + 1 + data.length;
  const packet = [length, operand, 0x04, permanent ? 0x08 : 0x00, ...data];
  packet.push(calculateChecksum(packet));
  return new Uint8Array(packet);
}

export function buildBarcodeContent(operand, data = []) {
  const prefix = [0x2B, 0x4E, 0x2B, 0x53, 0x2D];
  const bytes = [...prefix, operand, ...data];
  return String.fromCharCode(...bytes);
}

export function buildBarcodeContentRaw(bytes) {
  return String.fromCharCode(...bytes);
}

export function toHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');
}

export function fromHex(str) {
  return str.replace(/0x/gi, '').split(/[\s,]+/).filter((s) => s.length > 0).map((s) => parseInt(s, 16));
}
